import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useForm } from 'react-hook-form';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { SupportTaskKind } from '@duke-hq/svc-api-db/client';
import { CALL_TASK_KINDS } from './utils/constants';

interface CompleteTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  taskKind: SupportTaskKind;
}

interface CompleteTaskFormData {
  notes: string;
  callSuccessful?: boolean;
}

export function CompleteTaskModal({ isOpen, onClose, taskId, taskKind }: CompleteTaskModalProps) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const isCallTask = CALL_TASK_KINDS.includes(taskKind);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CompleteTaskFormData>({
    defaultValues: {
      notes: '',
      callSuccessful: undefined,
    },
  });

  const completeTaskMutation = trpcAdmin.support.task.complete.useMutation({
    onSuccess: () => {
      toast({
        status: 'success',
        title: t`Task completed successfully`,
      });
      void utils.support.task.get.invalidate();
      void utils.support.task.list.invalidate(undefined, { refetchType: 'active' });
      reset();
      onClose();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to complete task`,
        description: error.message,
      });
    },
  });

  const onSubmit = (data: CompleteTaskFormData) => {
    completeTaskMutation.mutate({
      taskId,
      notes: data.notes || undefined,
      callSuccessful: data.callSuccessful,
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const callSuccessful = watch('callSuccessful');

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>
          <Trans>Complete Task</Trans>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {isCallTask ? (
              <FormControl isRequired>
                <FormLabel>
                  <Trans>Call successful?</Trans>
                </FormLabel>
                <RadioGroup
                  value={callSuccessful === undefined ? '' : callSuccessful ? 'yes' : 'no'}
                  onChange={(value) => setValue('callSuccessful', value === 'yes')}
                >
                  <HStack spacing={4}>
                    <Radio value="yes">
                      <Trans>Yes</Trans>
                    </Radio>
                    <Radio value="no">
                      <Trans>No</Trans>
                    </Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>
            ) : null}

            <FormControl isInvalid={!!errors.notes}>
              <FormLabel>
                <Trans>Notes</Trans>
              </FormLabel>
              <Textarea
                {...register('notes')}
                placeholder={t`Add any notes about this task...`}
                rows={4}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={2}>
            <Button variant="ghost" onClick={handleClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={isSubmitting || completeTaskMutation.isPending}
            >
              <Trans>Complete Task</Trans>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
